import React, { Fragment, useContext, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import truncateMiddle from 'truncate-middle'
import { UserCompiledQuestContext } from '../../../../containers/Student/contexts'
import { gbInBytes, validateFileSize } from '../../../../utils/file'
import withApiData from '../../../HOC/withApiData'
import FlexContainer from '../../../styled/FlexContainer'
import FlexAlignCenter from '../../../styled/FlexAlignCenter'
import FileUploadProgressBar from './FileUploadProgressBar'
import {
  FileUploaderWrapper,
  CancelFileUploadButton,
  UploadedFilename,
  FileInput,
  FileSizeLimit
} from './styled'
import useLexicon from '../../../../hooks/useLexicon'

const defaultState = fileName => ({
  failure: null,
  fileName: fileName || '',
  resourceId: null,
  fileSizeExceeded: false,
  isUploading: false,
  isUploaded: false,
  progress: 0,
  xhr: null
})

const FileUploader = ({
  apiData,
  isEditing,
  fileName,
  setPatchId,
  onCancelUpload,
  setIsUploading,
  makeShell,
  patchShell,
  isSaving
}) => {
  const {
    ids: {
      courseSectionId,
      levelId
    },
    compiledQuest
  } = useContext(UserCompiledQuestContext)

  const lexicon = useLexicon()
  const [state, setState] = useState(defaultState(fileName))

  const updateProgress = event => {
    if (event.lengthComputable) {
      const progress = parseInt((event.loaded / event.total) * 100)
      setState(s => ({
        ...s,
        ...{
          progress
        }
      }))
    }
  }

  const onUpload = async e => {
    const uploadedResource = await makeShell()

    setPatchId(uploadedResource.id)

    const file = e.target.files[0]
    const fileName = file.name
    const objSon = uploadedResource.presigned_url
    const url = objSon.url

    const timestampedFilename = `${uploadedResource.unique_upload_file_name}.${
      /[^.]+$/.exec(fileName)[0]
    }`

    // validate file size. return and setState if too big
    if (!validateFileSize(e.target.files[0], gbInBytes(1))) {
      setState(s => ({
        ...s,
        ...{
          fileSizeExceeded: true,
          fileName: file.name
        }
      }))
      onCancelUpload(uploadedResource.id)
      e.target.value = null
      return
    }

    objSon.url_fields.key = objSon.url_fields.key.replace('${filename}', timestampedFilename)
    let xhr = new XMLHttpRequest()
    let fd = new FormData()

    xhr.upload.addEventListener('progress', updateProgress, false)

    await xhr.open('POST', url, true)

    setIsUploading(true)
    setState(s => ({
      ...s,
      ...{
        isUploading: true,
        failure: null,
        fileName: fileName,
        resourceId: uploadedResource.id,
        fileSizeExceeded: false,
        xhr
      }
    }))

    Object.entries(objSon.url_fields).forEach(([key, value]) => {
      fd.append(key, value)
    })

    fd.append('Content-Type', file.type)
    fd.append('file', file)
    xhr.send(fd)
    xhr.file = file
    xhr.fileName = fileName
    xhr.onload = async () => {
      if (xhr.status === 201) {
        patchShell(
          uploadedResource.id,
          {
            resource_file: timestampedFilename,
            original_file_name: file.name,
            file_size: file.size,
            content_type: file.type
          }
        )
        setState(s => ({
          ...s,
          ...{ isUploaded: true }
        }))
      } else if (xhr.status === 403) {
        setState(() => ({
          ...defaultState,
          failure: { type: 'expired' }
        }))
        onCancelUpload(uploadedResource.id)
      } else {
        setState(() => ({
          ...defaultState,
          failure: { type: 'generic' }
        }))
        onCancelUpload(uploadedResource.id)
      }
      setIsUploading(false)
    }

    xhr.onerror = e => {
      setState(() => ({
        ...defaultState,
        failure: { type: 'generic' }
      }))
      setIsUploading(false)
      onCancelUpload(uploadedResource.id)
    }
  }

  const abortRequest = async () => {
    let confirmation = window.confirm('Are you sure you want to cancel upload?')
    if (confirmation) {
      await state.xhr.abort()
      setState(defaultState)
      setIsUploading(false)
      onCancelUpload(state.resourceId)
    }
  }

  return (
    <FileUploaderWrapper>
      {isEditing || state.isUploaded ? (
        <UploadedFilename>{state.fileName}</UploadedFilename>
      ) : (state.isUploading || isSaving) ? (
        <div>
          <div className='caption1' style={{ color: '#000', marginBottom: '10px' }}>
            {state.fileName}
          </div>
          <FileUploadProgressBar progress={state.progress} />
          <FlexAlignCenter style={{width: '100%', justifyContent: 'space-between', marginTop: '8px' }}>
            <div className='caption1'>
              Please, keep this page open until upload is complete to save this {lexicon.resource.toLowerCase()}
            </div>
            <CancelFileUploadButton onClick={abortRequest}>
              Cancel
            </CancelFileUploadButton>
          </FlexAlignCenter>
        </div>
      ) : (
        <Fragment>
          <FileInput
            name='resource-file' id='resource-file'
            onChange={onUpload}
          />
          <FileSizeLimit emphasize={state.fileSizeExceeded}>
            File size limit is 1 GB
          </FileSizeLimit>
        </Fragment>
      )}
    </FileUploaderWrapper>
  )
}

FileUploader.propTypes = {
  apiData: PropTypes.object.isRequired,
  userQuestActivity: PropTypes.object.isRequired,
  setArtifact: PropTypes.func.isRequired,
  onCancelUpload: PropTypes.func.isRequired
}

export default withApiData(FileUploader)
